import { ROUTES } from 'config/routes';
import { useFormikContext } from 'formik';
import React from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { postAssessment } from 'store/assessmentSingle/actions';
import { IAppState } from 'store/index';

interface IProps {
  isOpen: boolean;
  setIsOpen: any;
  data: any;
  changedFieldsId: any;
  setChangedFieldsId: any;
  setUserHandleSaveData: any;
}

const SavePopup = ({
  isOpen,
  setIsOpen,
  changedFieldsId,
  setChangedFieldsId,
  setUserHandleSaveData,
  data,
}: IProps) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const originalData = useSelector(
    (state: IAppState) => state.singleAssessment.originalData,
  );

  const { t } = useTranslation();
  const onExit = () => {
    setIsOpen(false);
    history.push(ROUTES.assessments);
  };
  const { errors, values } = useFormikContext();

  const onSave = async () => {
    const dataToPost = [];
    data?.Pages?.forEach(p =>
      p.Fields.forEach(f => {
        if (f.Template.FieldType.Name === 'Grid') {
          // Find original grid field by Template ID across all pages
          let originalGridField = null;
          originalData?.[0]?.Pages?.forEach(originalPage => {
            const foundField = originalPage.Fields?.find(originalField => 
              originalField.Template.ID === f.Template.ID
            );
            if (foundField) {
              originalGridField = foundField;
            }
          });
          
          const gridField = { ...f };
          
          // Preserve existing IDs and generate new ones only for new fields
          gridField.GridChildren = f.GridChildren.map((row, rowIndex) => 
            row.map((field, fieldIndex) => {
              if (field.ID && typeof field.ID === 'number' && field.ID > 0) {
                return { ...field };
              }
              
              const originalFieldsMap = new Map();
              if (originalGridField?.GridChildren) {
                originalGridField.GridChildren.forEach((origRow, origRowIndex) => {
                  origRow.forEach(origField => {
                    const key = `${origField.Template.ID}_${origField.GridRow}`;
                    originalFieldsMap.set(key, origField);
                  });
                });
              }
              
              const key = `${field.Template.ID}_${field.GridRow}`;
              const originalField = originalFieldsMap.get(key);
              
              if (originalField && originalField.ID) {
                return { ...field, ID: originalField.ID };
              }
              
              const foundByTemplate = originalGridField?.GridChildren?.flat()?.find(origField => 
                origField.Template.ID === field.Template.ID
              );
              
              if (foundByTemplate && foundByTemplate.ID) {
                return { ...field, ID: foundByTemplate.ID };
              }
              
              return { ...field, ID: Date.now() + Math.random() };
            })
          );


          if (changedFieldsId.includes(f.Guid)) {
            const cleanGridField = {
              ID: gridField.ID,
              Guid: gridField.Guid,
              GridRow: gridField.GridRow,
              GridColumn: gridField.GridColumn,
              Value: gridField.Value,
              Template: {
                ID: gridField.Template.ID,
                Name: gridField.Template.Name,
                DisplayName: gridField.Template.DisplayName,
                Required: gridField.Template.Required,
                Order: gridField.Template.Order,
                HelpText: gridField.Template.HelpText,
                GridChild: gridField.Template.GridChild,
                FieldAlias: gridField.Template.FieldAlias,
                FieldType_ID: gridField.Template.FieldType_ID,
                FieldType: {
                  ID: gridField.Template.FieldType.ID,
                  Name: gridField.Template.FieldType.Name,
                  Discriminator: gridField.Template.FieldType.Discriminator
                },
                Parameters_ID: gridField.Template.Parameters_ID,
                Parameters: gridField.Template.Parameters || {
                  ID: gridField.Template.Parameters_ID,
                  Expression: null,
                  ValuesListID: 0,
                  MultiLine: false,
                  Discriminator: null,
                  MeasureDetail_ID: 0,
                  ShowRowsAsColumns: false,
                  FieldTemplateToMirror_ID: 0
                },
                Validator_ID: gridField.Template.Validator_ID,
                Validator: gridField.Template.Validator || {
                  ID: gridField.Template.Validator_ID,
                  MinimumDate: null,
                  MaximumDate: null,
                  MinRows: 0,
                  MaxRows: 0,
                  AllRowsVisible: false,
                  Minimum: 0,
                  Maximum: 0,
                  IntegerOnly: false,
                  MinimumLength: 0,
                  MaximumLength: 0,
                  Discriminator: null
                },
                OriginalSize: gridField.Template.OriginalSize,
                OriginalWidth: gridField.Template.OriginalWidth,
                OriginalHeight: gridField.Template.OriginalHeight,
                MaintainAspectRatio: gridField.Template.MaintainAspectRatio,
                InitiallyVisibleOnLoad: gridField.Template.InitiallyVisibleOnLoad,
                VisibleExternally: gridField.Template.VisibleExternally,
                Archived: gridField.Template.Archived,
                ImageOnly: gridField.Template.ImageOnly,
                HasImage: gridField.Template.HasImage
              },
              GridChildren: gridField.GridChildren.map(row => 
                row.map(field => ({
                  ID: field.ID,
                  Guid: field.Guid,
                  GridRow: field.GridRow,
                  GridColumn: field.GridColumn,
                  Value: field.Value,
                  Template: {
                    ID: field.Template.ID,
                    Name: field.Template.Name,
                    DisplayName: field.Template.DisplayName,
                    Required: field.Template.Required,
                    Order: field.Template.Order,
                    HelpText: field.Template.HelpText,
                    GridChild: field.Template.GridChild,
                    FieldAlias: field.Template.FieldAlias,
                    FieldType_ID: field.Template.FieldType_ID,
                    FieldType: {
                      ID: field.Template.FieldType.ID,
                      Name: field.Template.FieldType.Name,
                      Discriminator: field.Template.FieldType.Discriminator
                    },
                    Parameters_ID: field.Template.Parameters_ID,
                    Parameters: field.Template.Parameters || {
                      ID: field.Template.Parameters_ID,
                      Expression: null,
                      ValuesListID: 0,
                      MultiLine: false,
                      Discriminator: null,
                      MeasureDetail_ID: 0,
                      ShowRowsAsColumns: false,
                      FieldTemplateToMirror_ID: 0
                    },
                    Validator_ID: field.Template.Validator_ID,
                    Validator: field.Template.Validator || {
                      ID: field.Template.Validator_ID,
                      MinimumDate: null,
                      MaximumDate: null,
                      MinRows: 0,
                      MaxRows: 0,
                      AllRowsVisible: false,
                      Minimum: 0,
                      Maximum: 0,
                      IntegerOnly: false,
                      MinimumLength: 0,
                      MaximumLength: 0,
                      Discriminator: null
                    },
                    OriginalSize: field.Template.OriginalSize,
                    OriginalWidth: field.Template.OriginalWidth,
                    OriginalHeight: field.Template.OriginalHeight,
                    MaintainAspectRatio: field.Template.MaintainAspectRatio,
                    InitiallyVisibleOnLoad: field.Template.InitiallyVisibleOnLoad,
                    VisibleExternally: field.Template.VisibleExternally,
                    Archived: field.Template.Archived,
                    ImageOnly: field.Template.ImageOnly,
                    HasImage: field.Template.HasImage
                  },
                  GridChildren: field.GridChildren || [],
                  Invalid: field.Invalid
                }))
              ),
              Invalid: gridField.Invalid
            };
            
            dataToPost.push(cleanGridField);
          }
        } else {
          if (changedFieldsId.includes(f.Guid)) {
            dataToPost.push({
              ...f,
              Value: values[f.Guid] as string,
            });
          }
        }
      }),
    );
    await dispatch(postAssessment(dataToPost, false));
    setChangedFieldsId([]);
    setUserHandleSaveData(true);
    setIsOpen(false);
    history.push(ROUTES.assessments);
  };
  return (
    <Modal centered show={isOpen} className="modal-small save-changes-modal">
      <div className="modal-content">
        <Modal.Header className="modal-header" translate="">
          <h5 className="modal-title">{t('AP_cont?')}</h5>
        </Modal.Header>
        <Modal.Footer className="modal-footer">
          <div className="container-fluid">
            <div className="row">
              <div className="col-6">
                <button
                  type="button"
                  className="btn action-secondary"
                  data-dismiss="modal"
                  onClick={onExit}
                >
                  {t('AP_Exit')}
                </button>
              </div>
              <div className="col-6">
                <button
                  type="button"
                  className="btn action-primary"
                  onClick={onSave}
                >
                  {t('AP_save')}
                </button>
              </div>
            </div>
          </div>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default SavePopup;
